import React, { useState, useEffect, useMemo } from 'react';
import { 
  Save, 
  RefreshCw, 
  Shield, 
  Mail, 
  Calendar,
  Database,
  Download,
  Server,
  Lock,
  Bell,
  Globe
} from 'lucide-react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { workspaceAdminAPI } from '../../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { CardSkeleton, EmptyState } from '../common/States';

const SYSTEM_SECTIONS = [
  {
    key: 'general',
    id: 'system/general',
    label: 'General',
    description: 'Manage workspace identity, support, and locale defaults',
  },
  {
    key: 'security',
    id: 'system/security',
    label: 'Security',
    description: 'Control password, session, and authentication policy',
  },
  {
    key: 'email',
    id: 'system/email',
    label: 'Email',
    description: 'Configure SMTP delivery and notification settings',
  },
  {
    key: 'limits',
    id: 'system/limits',
    label: 'System Limits',
    description: 'Set operational limits, maintenance, and audit flags',
  },
  {
    key: 'backup',
    id: 'system/backup',
    label: 'Backup & Recovery',
    description: 'Review backup health and run recovery operations',
  },
];

const SystemSettings = ({ activeTab = 'system/general', onNavigate }) => {
  // Field shape only. Every default value now comes from the database — the
  // model's own column defaults, served by /api/platform-definitions/tenant-settings/defaults.
  // Restating them here is what let the screen fall back to 'Asia/Kolkata' while
  // the column default said 'UTC'.
  const [settings, setSettings] = useState({
    app_name: '',
    company_name: '',
    default_timezone: '',
    date_format: 'DD/MM/YYYY',
    display_name: '',
    legal_name: '',
    support_email: '',
    support_phone: '',
    website_url: '',
    address: '',
    payroll_note: '',
    mark_setup_complete: false,
    smtp_host: '',
    smtp_username: '',
    smtp_password: '',
    email_from: '',
  });
  
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingLeavePolicies, setSyncingLeavePolicies] = useState(false);
  const [message, setMessage] = useState('');
  const [leaveSyncSummary, setLeaveSyncSummary] = useState(null);
  const [showLeaveSyncSummary, setShowLeaveSyncSummary] = useState(false);
  const [backupSummary, setBackupSummary] = useState(null);
  const [backupRuns, setBackupRuns] = useState([]);
  const [backupTenants, setBackupTenants] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [selectedBackupTenantId, setSelectedBackupTenantId] = useState('');

  useEffect(() => {
    loadSettings();
    loadBackupData();
  }, []);

  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^system\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'system') {
      return 'general';
    }
    return SYSTEM_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'general';
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [defaultsResponse, response] = await Promise.all([
        api.get('/api/platform-definitions/tenant-settings/defaults').catch(() => null),
        workspaceAdminAPI.getSettings(),
      ]);
      const defaults = defaultsResponse?.data?.defaults || {};
      setDefaults(defaults);
      if (response.data) {
        const normalizedSettings = {
          ...defaults,
          ...response.data,
          app_name: response.data?.display_name || '',
          company_name: response.data?.legal_name || '',
          default_timezone: response.data?.timezone || defaults.timezone || '',
          smtp_password: '', // secret is never returned; keep the field controlled
        };
        setSettings(prev => ({ ...prev, ...normalizedSettings }));
      } else {
        setSettings(prev => ({ ...prev, ...defaults }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      const payload = {
        display_name: String(settings.app_name || settings.display_name || '').trim(),
        legal_name: String(settings.company_name || settings.legal_name || '').trim() || null,
        support_email: String(settings.support_email || '').trim() || null,
        support_phone: String(settings.support_phone || '').trim() || null,
        website_url: String(settings.website_url || '').trim() || null,
        address: String(settings.address || '').trim() || null,
        timezone: String(settings.default_timezone || settings.timezone || defaults.timezone || '').trim(),
        currency_code: String(settings.currency_code || defaults.currency_code || '').trim().toUpperCase(),
        payroll_note: String(settings.payroll_note || '').trim() || null,
        request_mutation_window_hours: Number.parseInt(String(settings.request_mutation_window_hours ?? defaults.request_mutation_window_hours ?? ''), 10) || undefined,
        api_timeout_seconds: Number.parseInt(String(settings.api_timeout_seconds ?? defaults.api_timeout_seconds ?? ''), 10) || undefined,
        mark_setup_complete: Boolean(settings.mark_setup_complete),
        // Email / SMTP
        smtp_host: String(settings.smtp_host || '').trim() || null,
        smtp_port: Number.parseInt(String(settings.smtp_port ?? defaults.smtp_port ?? ''), 10) || undefined,
        smtp_username: String(settings.smtp_username || '').trim() || null,
        smtp_password: settings.smtp_password ? String(settings.smtp_password) : null,
        email_from: String(settings.email_from || '').trim() || null,
        enable_email_notifications: Boolean(settings.enable_email_notifications),
        // Operational limits & flags
        max_file_size_mb: Number.parseInt(String(settings.max_file_size_mb ?? defaults.max_file_size_mb ?? ''), 10) || undefined,
        backup_retention_days: Number.parseInt(String(settings.backup_retention_days ?? defaults.backup_retention_days ?? ''), 10) || undefined,
        allowed_file_types: String(settings.allowed_file_types || '').trim() || null,
        enable_audit_logging: Boolean(settings.enable_audit_logging),
        enable_maintenance_mode: Boolean(settings.enable_maintenance_mode),
        // Security policy
        password_min_length: Number.parseInt(String(settings.password_min_length ?? defaults.password_min_length ?? ''), 10) || undefined,
        session_timeout_minutes: Number.parseInt(String(settings.session_timeout_minutes ?? defaults.session_timeout_minutes ?? ''), 10) || undefined,
        max_login_attempts: Number.parseInt(String(settings.max_login_attempts ?? defaults.max_login_attempts ?? ''), 10) || undefined,
        password_require_special: Boolean(settings.password_require_special),
        password_require_number: Boolean(settings.password_require_number),
        password_require_uppercase: Boolean(settings.password_require_uppercase),
        enable_two_factor: Boolean(settings.enable_two_factor),
        // Attendance integrity. Every one of these is off until somebody here
        // turns it on — an auto punch-out is a payroll write.
        auto_close_on_shift_end: Boolean(settings.auto_close_on_shift_end),
        auto_close_grace_minutes: Number.parseInt(String(settings.auto_close_grace_minutes ?? 60), 10) || 0,
        auto_close_on_silence: Boolean(settings.auto_close_on_silence),
        presence_silence_minutes: Number.parseInt(String(settings.presence_silence_minutes ?? 45), 10) || 45,
        auto_close_on_geofence_exit: Boolean(settings.auto_close_on_geofence_exit),
        geofence_exit_margin_meters: Number.parseFloat(String(settings.geofence_exit_margin_meters ?? 50)) || 0,
        geofence_exit_dwell_minutes: Number.parseInt(String(settings.geofence_exit_dwell_minutes ?? 10), 10) || 10,
        lock_on_location_spoofing: Boolean(settings.lock_on_location_spoofing),
      };
      const response = await workspaceAdminAPI.updateSettings(payload);
      if (response.data) {
        const normalizedSettings = {
          ...response.data,
          app_name: response.data?.display_name || '',
          company_name: response.data?.legal_name || '',
          default_timezone: response.data?.timezone || '',
          smtp_password: '', // secret is never returned; keep the field controlled
        };
        setSettings((prev) => ({ ...prev, ...normalizedSettings }));
      }
      setMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleNumberInputChange = (key, value) => {
    const nextValue = Number.parseInt(value, 10);
    setSettings((prev) => ({
      ...prev,
      [key]: Number.isNaN(nextValue) ? '' : nextValue,
    }));
  };

  const handleSwitchChange = (key, checked) => {
    setSettings(prev => ({ ...prev, [key]: checked }));
  };

  const handleResetToDefaults = async () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      try {
        setLoading(true);
        await workspaceAdminAPI.resetSystemSettings();
        await loadSettings();
        setMessage('Settings reset to defaults');
      } catch (error) {
        console.error('Failed to reset settings:', error);
        setMessage('Failed to reset settings');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSyncStandardLeavePolicies = async () => {
    try {
      setSyncingLeavePolicies(true);
      const response = await workspaceAdminAPI.seedStandardLeavePolicies(true);
      const summary = response.data || {};
      setLeaveSyncSummary(summary);
      setShowLeaveSyncSummary(true);
      toast.success(
        `Leave policy sync complete: ${summary.tenants_processed || 0} tenants, ${summary.created_policies || 0} created, ${summary.updated_policies || 0} updated.`
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync standard leave policies'));
    } finally {
      setSyncingLeavePolicies(false);
    }
  };

  const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const loadBackupData = async () => {
    try {
      setBackupLoading(true);
      const [summaryResponse, runsResponse, tenantsResponse] = await Promise.all([
        workspaceAdminAPI.getBackupSummary(),
        workspaceAdminAPI.listBackupRuns({ limit: 25 }),
        workspaceAdminAPI.listTenants(),
      ]);
      setBackupSummary(summaryResponse.data || null);
      setBackupRuns(Array.isArray(runsResponse.data) ? runsResponse.data : []);
      setBackupTenants(Array.isArray(tenantsResponse.data) ? tenantsResponse.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load backup dashboard'));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRunPlatformBackup = async () => {
    if (!window.confirm('Run a full platform database backup now?')) return;
    try {
      setBackupRunning(true);
      await workspaceAdminAPI.runBackup({ scope: 'platform', backup_type: 'full', include_files: false });
      toast.success('Backup started');
      await loadBackupData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run backup'));
    } finally {
      setBackupRunning(false);
    }
  };

  const handleRunTenantBackup = async () => {
    const tenantId = selectedBackupTenantId ? Number.parseInt(selectedBackupTenantId, 10) : null;
    if (!tenantId) {
      toast.error('Select a tenant');
      return;
    }
    if (!window.confirm('Run a tenant backup now?')) return;
    try {
      setBackupRunning(true);
      await workspaceAdminAPI.runBackup({ scope: 'tenant', backup_type: 'incremental', tenant_id: tenantId, include_files: false });
      toast.success('Backup started');
      await loadBackupData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run tenant backup'));
    } finally {
      setBackupRunning(false);
    }
  };

  const handleRunUploadsBackup = async () => {
    const tenantId = selectedBackupTenantId ? Number.parseInt(selectedBackupTenantId, 10) : null;
    if (!window.confirm('Run an uploads backup now?')) return;
    try {
      setBackupRunning(true);
      await workspaceAdminAPI.runBackup({
        scope: tenantId ? 'tenant' : 'platform',
        backup_type: 'files',
        tenant_id: tenantId || null,
        include_files: true,
      });
      toast.success('Backup started');
      await loadBackupData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run uploads backup'));
    } finally {
      setBackupRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-1/4 rounded-md shimmer" />
        {[...Array(4)].map((_, i) => (
          <CardSkeleton key={i} rows={5} />
        ))}
      </div>
    );
  }

  const renderGeneralSection = () => (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>Manage workspace identity, support contacts, and locale defaults.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="app_name">Application Name</Label>
            <Input
              id="app_name"
              value={settings.app_name}
              onChange={(e) => handleInputChange('app_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_name">Company Name</Label>
            <Input
              id="company_name"
              value={settings.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="support_email">Support Email</Label>
            <Input
              id="support_email"
              type="email"
              value={settings.support_email}
              onChange={(e) => handleInputChange('support_email', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="default_timezone">Default Timezone</Label>
            <select
              id="default_timezone"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={settings.default_timezone}
              onChange={(e) => handleInputChange('default_timezone', e.target.value)}
            >
              <option value="Asia/Kolkata">India (Asia/Kolkata)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">New York (EST)</option>
              <option value="Europe/London">London (GMT)</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="date_format">Date Format</Label>
          <select
            id="date_format"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={settings.date_format}
            onChange={(e) => handleInputChange('date_format', e.target.value)}
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </CardContent>
    </Card>
  );

  const renderSecuritySection = () => (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Security Settings</CardTitle>
            <CardDescription>Control password, session, and authentication policy.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="password_min_length">Minimum Password Length</Label>
            <Input
              id="password_min_length"
              type="number"
              min="6"
              max="20"
              value={settings.password_min_length}
              onChange={(e) => handleInputChange('password_min_length', parseInt(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="session_timeout_minutes">Session Timeout (minutes)</Label>
            <Input
              id="session_timeout_minutes"
              type="number"
              min="5"
              max="480"
              value={settings.session_timeout_minutes}
              onChange={(e) => handleInputChange('session_timeout_minutes', parseInt(e.target.value))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="max_login_attempts">Max Login Attempts</Label>
            <Input
              id="max_login_attempts"
              type="number"
              min="1"
              max="10"
              value={settings.max_login_attempts}
              onChange={(e) => handleInputChange('max_login_attempts', parseInt(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="api_timeout_seconds">API Timeout Threshold (seconds)</Label>
            <Input
              id="api_timeout_seconds"
              type="number"
              min="5"
              max="300"
              value={settings.api_timeout_seconds ?? ''}
              onChange={(e) => handleNumberInputChange('api_timeout_seconds', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="password_require_special">Require Special Characters</Label>
              <p className="text-sm text-muted-foreground">
                Passwords must contain special characters (!@#$%^&*)
              </p>
            </div>
            <Switch
              id="password_require_special"
              checked={settings.password_require_special}
              onCheckedChange={(checked) => handleSwitchChange('password_require_special', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="password_require_number">Require Numbers</Label>
              <p className="text-sm text-muted-foreground">
                Passwords must contain at least one number
              </p>
            </div>
            <Switch
              id="password_require_number"
              checked={settings.password_require_number}
              onCheckedChange={(checked) => handleSwitchChange('password_require_number', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="password_require_uppercase">Require Uppercase Letters</Label>
              <p className="text-sm text-muted-foreground">
                Passwords must contain at least one uppercase letter
              </p>
            </div>
            <Switch
              id="password_require_uppercase"
              checked={settings.password_require_uppercase}
              onCheckedChange={(checked) => handleSwitchChange('password_require_uppercase', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable_two_factor">Enable Two-Factor Authentication</Label>
              <p className="text-sm text-muted-foreground">
                Require 2FA for all user accounts
              </p>
            </div>
            <Switch
              id="enable_two_factor"
              checked={settings.enable_two_factor}
              onCheckedChange={(checked) => handleSwitchChange('enable_two_factor', checked)}
            />
          </div>
        </div>

        {/* Attendance integrity. Kept in the security section rather than the
            attendance one because two of the three consequences are security
            decisions — and the third takes money off a payslip. */}
        <div className="space-y-4 pt-6 border-t border-border">
          <div>
            <h4 className="text-sm font-semibold text-foreground">Automatic punch-out</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Closes a shift nobody closed. Every switch here changes what appears on a
              payslip, so each one is off until you turn it on — and the employee is told
              whenever the system closes their day.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto_close_on_shift_end">Close forgotten punches after the shift</Label>
              <p className="text-sm text-muted-foreground">
                The safe one: it cannot fire during a shift. Closes at the last evidence
                of presence, or at the rostered end time when there is none.
              </p>
            </div>
            <Switch
              id="auto_close_on_shift_end"
              checked={Boolean(settings.auto_close_on_shift_end)}
              onCheckedChange={(checked) => handleSwitchChange('auto_close_on_shift_end', checked)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="auto_close_grace_minutes">Grace after shift end (minutes)</Label>
              <Input
                id="auto_close_grace_minutes"
                type="number"
                min="0"
                max="720"
                value={settings.auto_close_grace_minutes ?? 60}
                onChange={(e) => handleNumberInputChange('auto_close_grace_minutes', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                How long someone may stay past their shift before the sweep acts.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="presence_silence_minutes">Silence before closing (minutes)</Label>
              <Input
                id="presence_silence_minutes"
                type="number"
                min="30"
                max="480"
                value={settings.presence_silence_minutes ?? 45}
                onChange={(e) => handleNumberInputChange('presence_silence_minutes', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Minimum 30. Shorter cannot be trusted — a phone in a lift looks identical
                to one that was switched off.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto_close_on_silence">Close mid-shift when a device goes quiet</Label>
              <p className="text-sm text-muted-foreground">
                Only applies to Android devices running continuous monitoring. iPhones are
                never closed this way — iOS withholds background time, and reading that as
                someone going home would be wrong every day.
              </p>
            </div>
            <Switch
              id="auto_close_on_silence"
              checked={Boolean(settings.auto_close_on_silence)}
              onCheckedChange={(checked) => handleSwitchChange('auto_close_on_silence', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto_close_on_geofence_exit">Close when a device leaves the site</Label>
              <p className="text-sm text-muted-foreground">
                Needs at least one geofence. Field, remote and hybrid staff are exempt, and
                a device with no usable fix is never treated as having left.
              </p>
            </div>
            <Switch
              id="auto_close_on_geofence_exit"
              checked={Boolean(settings.auto_close_on_geofence_exit)}
              onCheckedChange={(checked) => handleSwitchChange('auto_close_on_geofence_exit', checked)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="geofence_exit_margin_meters">Exit margin (metres)</Label>
              <Input
                id="geofence_exit_margin_meters"
                type="number"
                min="0"
                max="5000"
                value={settings.geofence_exit_margin_meters ?? 50}
                onChange={(e) => handleInputChange('geofence_exit_margin_meters', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Added to the fence radius so a desk near the boundary does not flip in and
                out all day.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="geofence_exit_dwell_minutes">Must be outside for (minutes)</Label>
              <Input
                id="geofence_exit_dwell_minutes"
                type="number"
                min="1"
                max="240"
                value={settings.geofence_exit_dwell_minutes ?? 10}
                onChange={(e) => handleNumberInputChange('geofence_exit_dwell_minutes', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Walking to the car park should not end a shift.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="lock_on_location_spoofing">Lock the account on a faked location</Label>
              <p className="text-sm text-muted-foreground">
                Only a mock-location provider locks; a rooted or jailbroken phone is flagged
                for review and never locked. Detectable on Android only — an iPhone cannot
                be checked for this. A workspace admin unlocks from Session Governance.
              </p>
            </div>
            <Switch
              id="lock_on_location_spoofing"
              checked={Boolean(settings.lock_on_location_spoofing)}
              onCheckedChange={(checked) => handleSwitchChange('lock_on_location_spoofing', checked)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderEmailSection = () => (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Email Settings</CardTitle>
            <CardDescription>Configure SMTP delivery and notification preferences.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="smtp_host">SMTP Host</Label>
            <Input
              id="smtp_host"
              value={settings.smtp_host}
              onChange={(e) => handleInputChange('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp_port">SMTP Port</Label>
            <Input
              id="smtp_port"
              type="number"
              value={settings.smtp_port}
              onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="smtp_username">SMTP Username</Label>
            <Input
              id="smtp_username"
              value={settings.smtp_username}
              onChange={(e) => handleInputChange('smtp_username', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp_password">SMTP Password</Label>
            <Input
              id="smtp_password"
              type="password"
              value={settings.smtp_password}
              onChange={(e) => handleInputChange('smtp_password', e.target.value)}
              placeholder={settings.smtp_password_set ? 'Configured — leave blank to keep' : ''}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email_from">From Email Address</Label>
          <Input
            id="email_from"
            type="email"
            value={settings.email_from}
            onChange={(e) => handleInputChange('email_from', e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable_email_notifications">Enable Email Notifications</Label>
            <p className="text-sm text-muted-foreground">
              Send email notifications for system events
            </p>
          </div>
          <Switch
            id="enable_email_notifications"
            checked={settings.enable_email_notifications}
            onCheckedChange={(checked) => handleSwitchChange('enable_email_notifications', checked)}
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderLimitsSection = () => (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>System Limits</CardTitle>
            <CardDescription>Set operational limits, maintenance, and audit flags.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="max_file_size_mb">Max File Size (MB)</Label>
            <Input
              id="max_file_size_mb"
              type="number"
              min="1"
              max="100"
              value={settings.max_file_size_mb}
              onChange={(e) => handleInputChange('max_file_size_mb', parseInt(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="backup_retention_days">Backup Retention (days)</Label>
            <Input
              id="backup_retention_days"
              type="number"
              min="1"
              max="365"
              value={settings.backup_retention_days}
              onChange={(e) => handleInputChange('backup_retention_days', parseInt(e.target.value))}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="allowed_file_types">Allowed File Types</Label>
          <Input
            id="allowed_file_types"
            value={settings.allowed_file_types}
            onChange={(e) => handleInputChange('allowed_file_types', e.target.value)}
            placeholder="pdf,doc,docx,jpg,jpeg,png"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable_audit_logging">Enable Audit Logging</Label>
            <p className="text-sm text-muted-foreground">
              Log all system activities for security monitoring
            </p>
          </div>
          <Switch
            id="enable_audit_logging"
            checked={settings.enable_audit_logging}
            onCheckedChange={(checked) => handleSwitchChange('enable_audit_logging', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable_maintenance_mode">Maintenance Mode</Label>
            <p className="text-sm text-muted-foreground">
              Put the system in maintenance mode (users will see a maintenance page)
            </p>
          </div>
          <Switch
            id="enable_maintenance_mode"
            checked={settings.enable_maintenance_mode}
            onCheckedChange={(checked) => handleSwitchChange('enable_maintenance_mode', checked)}
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderBackupSection = () => (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Backup & Recovery</CardTitle>
            <CardDescription>Review backup health and run platform or tenant recovery operations.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border border-border/70 bg-background p-3">
              <p className="text-xs text-muted-foreground">Last Successful Backup</p>
              <p className="mt-1 text-sm font-semibold">
                {backupSummary?.last_successful_backup_at ? new Date(backupSummary.last_successful_backup_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div className="rounded-md border border-border/70 bg-background p-3">
              <p className="text-xs text-muted-foreground">Next Scheduled Backup</p>
              <p className="mt-1 text-sm font-semibold">
                {backupSummary?.next_scheduled_backup_at ? new Date(backupSummary.next_scheduled_backup_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div className="rounded-md border border-border/70 bg-background p-3">
              <p className="text-xs text-muted-foreground">Backup Health</p>
              <p className="mt-1 text-sm font-semibold">{backupSummary?.backup_health || 'Unknown'}</p>
            </div>
            <div className="rounded-md border border-border/70 bg-background p-3">
              <p className="text-xs text-muted-foreground">Storage Used (30d)</p>
              <p className="mt-1 text-sm font-semibold">{formatBytes(backupSummary?.storage_used_bytes_30d)}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={loadBackupData} disabled={backupLoading || backupRunning}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleRunPlatformBackup} disabled={backupLoading || backupRunning}>
              <Database className="h-4 w-4 mr-2" />
              Run Full DB Backup
            </Button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
          <div className="space-y-2">
            <Label htmlFor="backup-tenant-select">Tenant</Label>
            <select
              id="backup-tenant-select"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={selectedBackupTenantId}
              onChange={(e) => setSelectedBackupTenantId(e.target.value)}
            >
              <option value="">Select Tenant</option>
              {backupTenants.map((tenant) => (
                <option key={`backup-tenant-${tenant.id}`} value={String(tenant.id)}>
                  {tenant.name || tenant.slug || `Tenant #${tenant.id}`}
                </option>
              ))}
            </select>
          </div>

          <Button variant="outline" onClick={handleRunTenantBackup} disabled={backupLoading || backupRunning}>
            <Database className="h-4 w-4 mr-2" />
            Run Tenant Backup
          </Button>
          <Button variant="outline" onClick={handleRunUploadsBackup} disabled={backupLoading || backupRunning}>
            <Database className="h-4 w-4 mr-2" />
            Run Uploads Backup
          </Button>
        </div>

        <div className="rounded-md border border-border/70 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2">Run</th>
                <th className="text-left p-2">Scope</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Size</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Download</th>
              </tr>
            </thead>
            <tbody>
              {backupRuns.length === 0 ? (
                <tr className="border-t">
                  <td colSpan={7}>
                    <EmptyState
                      icon={Database}
                      compact
                      title="No backups yet"
                      description="Run a full DB, tenant, or uploads backup to see recovery points here."
                    />
                  </td>
                </tr>
              ) : (
                backupRuns.map((row) => (
                  <tr key={`backup-run-${row.id}`} className="border-t">
                    <td className="p-2">{row.id}</td>
                    <td className="p-2">{row.scope}</td>
                    <td className="p-2">{row.backup_type}</td>
                    <td className="p-2">{row.status}</td>
                    <td className="p-2">{formatBytes(row.bytes_size)}</td>
                    <td className="p-2">{row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'}</td>
                    <td className="p-2">
                      {row.download_url ? (
                        <a
                          href={row.download_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted hover:border-primary/40 hover:text-primary"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </a>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'security':
        return renderSecuritySection();
      case 'email':
        return renderEmailSection();
      case 'limits':
        return renderLimitsSection();
      case 'backup':
        return renderBackupSection();
      case 'general':
      default:
        return renderGeneralSection();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">System Settings</h2>
          <p className="text-muted-foreground">Configure global system preferences and security</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncStandardLeavePolicies} disabled={syncingLeavePolicies}>
            <Database className="h-4 w-4 mr-2" />
            {syncingLeavePolicies ? 'Syncing Leave Policies...' : 'Sync Leave Policies'}
          </Button>
          <Button variant="outline" onClick={handleResetToDefaults}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-md text-sm font-medium ${
          message.includes('Failed')
            ? 'bg-destructive/15 text-destructive'
            : 'bg-success/10 text-success'
        }`}>
          {message}
        </div>
      )}

      {currentSectionKey === 'general' ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {SYSTEM_SECTIONS.map((section) => {
            const isActive = section.key === currentSectionKey;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => onNavigate?.(section.id)}
                className={`rounded-xl border p-4 text-left transition-all ${
                  isActive
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-primary/40 hover:shadow-sm'
                }`}
              >
                <p className="text-sm font-semibold text-foreground">{section.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{section.description}</p>
              </button>
            );
          })}
        </div>
      ) : null}

      {renderActiveSection()}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backup & Recovery
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border border-border/70 bg-background p-3">
                <p className="text-xs text-muted-foreground">Last Successful Backup</p>
                <p className="mt-1 text-sm font-semibold">
                  {backupSummary?.last_successful_backup_at ? new Date(backupSummary.last_successful_backup_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="rounded-md border border-border/70 bg-background p-3">
                <p className="text-xs text-muted-foreground">Next Scheduled Backup</p>
                <p className="mt-1 text-sm font-semibold">
                  {backupSummary?.next_scheduled_backup_at ? new Date(backupSummary.next_scheduled_backup_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="rounded-md border border-border/70 bg-background p-3">
                <p className="text-xs text-muted-foreground">Backup Health</p>
                <p className="mt-1 text-sm font-semibold">{backupSummary?.backup_health || 'Unknown'}</p>
              </div>
              <div className="rounded-md border border-border/70 bg-background p-3">
                <p className="text-xs text-muted-foreground">Storage Used (30d)</p>
                <p className="mt-1 text-sm font-semibold">{formatBytes(backupSummary?.storage_used_bytes_30d)}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={loadBackupData} disabled={backupLoading || backupRunning}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleRunPlatformBackup} disabled={backupLoading || backupRunning}>
                <Database className="h-4 w-4 mr-2" />
                Run Full DB Backup
              </Button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="backup-tenant-select">Tenant</Label>
              <select
                id="backup-tenant-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={selectedBackupTenantId}
                onChange={(e) => setSelectedBackupTenantId(e.target.value)}
              >
                <option value="">Select Tenant</option>
                {backupTenants.map((tenant) => (
                  <option key={`backup-tenant-${tenant.id}`} value={String(tenant.id)}>
                    {tenant.name || tenant.slug || `Tenant #${tenant.id}`}
                  </option>
                ))}
              </select>
            </div>

            <Button variant="outline" onClick={handleRunTenantBackup} disabled={backupLoading || backupRunning}>
              <Database className="h-4 w-4 mr-2" />
              Run Tenant Backup
            </Button>
            <Button variant="outline" onClick={handleRunUploadsBackup} disabled={backupLoading || backupRunning}>
              <Database className="h-4 w-4 mr-2" />
              Run Uploads Backup
            </Button>
          </div>

          <div className="rounded-md border border-border/70 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2">Run</th>
                  <th className="text-left p-2">Scope</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Size</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Download</th>
                </tr>
              </thead>
              <tbody>
                {backupRuns.length === 0 ? (
                  <tr className="border-t">
                    <td className="p-3 text-muted-foreground" colSpan={7}>
                      No backups found.
                    </td>
                  </tr>
                ) : (
                  backupRuns.map((row) => (
                    <tr key={`backup-run-${row.id}`} className="border-t">
                      <td className="p-2">{row.id}</td>
                      <td className="p-2">{row.scope}</td>
                      <td className="p-2">{row.backup_type}</td>
                      <td className="p-2">{row.status}</td>
                      <td className="p-2">{formatBytes(row.bytes_size)}</td>
                      <td className="p-2">{row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'}</td>
                      <td className="p-2">
                        {row.download_url ? (
                          <a
                            href={row.download_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted hover:border-primary/40 hover:text-primary"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </a>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showLeaveSyncSummary} onOpenChange={setShowLeaveSyncSummary}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Leave Policy Sync Summary</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div className="border rounded-md p-3">
                <p className="text-muted-foreground">Tenants</p>
                <p className="font-semibold">{leaveSyncSummary?.tenants_processed ?? 0}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-muted-foreground">Policies Created</p>
                <p className="font-semibold">{leaveSyncSummary?.created_policies ?? 0}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-muted-foreground">Policies Updated</p>
                <p className="font-semibold">{leaveSyncSummary?.updated_policies ?? 0}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-muted-foreground">Assignments Created</p>
                <p className="font-semibold">{leaveSyncSummary?.employee_assignments_created ?? 0}</p>
              </div>
              <div className="border rounded-md p-3">
                <p className="text-muted-foreground">Assignments Updated</p>
                <p className="font-semibold">{leaveSyncSummary?.employee_assignments_updated ?? 0}</p>
              </div>
            </div>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2">Tenant ID</th>
                    <th className="text-left p-2">Created Policies</th>
                    <th className="text-left p-2">Updated Policies</th>
                    <th className="text-left p-2">Created Assignments</th>
                    <th className="text-left p-2">Updated Assignments</th>
                  </tr>
                </thead>
                <tbody>
                  {(leaveSyncSummary?.per_tenant || []).map((row) => (
                    <tr key={`tenant-sync-${row.tenant_id}`} className="border-t">
                      <td className="p-2">{row.tenant_id}</td>
                      <td className="p-2">{row.created_policies || 0}</td>
                      <td className="p-2">{row.updated_policies || 0}</td>
                      <td className="p-2">{row.employee_assignments_created || 0}</td>
                      <td className="p-2">{row.employee_assignments_updated || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemSettings;
